/**
 * Property detail · Revenue tab.
 *
 * Shows the active revenue model (full_rent or pct_of_rent — COL-222) and
 * the computed estimated margin. Read-only; edits flow through the
 * property edit sheet (COL-223).
 */
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import type { Property, Room } from "@/types/property";
import { formatEUR } from "@/lib/v2-format";
import { useLanguage } from "@/contexts/LanguageContext";
import { useOperatorBranding } from "@/hooks/useOperatorBranding";
import { usePropertyOccupiedRoomIds } from "@/hooks/v2/useProperties";

type Props = {
  property: Property;
  rooms: Room[];
};

export function RevenueTab({ property, rooms }: Props) {
  const { t } = useLanguage();
  // Operators bypass the CoLiviq-branded translation strings (COL-352).
  const { platformName, isOperator } = useOperatorBranding();
  // Bookings, not the stale rooms.status/available flags.
  const { data: occupiedRoomIds = new Set<number>() } =
    usePropertyOccupiedRoomIds(property.id);
  const occupiedRent = rooms
    .filter((r) => !r.is_hidden)
    .filter((r) => occupiedRoomIds.has(Number(r.id)))
    .reduce((s, r) => s + Number(r.price ?? 0), 0);
  const totalListedRent = rooms
    .filter((r) => !r.is_hidden)
    .reduce((s, r) => s + Number(r.price ?? 0), 0);

  const model = property.revenue_model ?? "full_rent";
  const isPct = model === "pct_of_rent";
  const masterRent = Number(property.master_rent ?? 0);
  const feePct = Number(property.coliviq_fee_pct ?? 0);

  // Margin by model:
  //   full_rent   : collected tenant rent − master rent
  //   pct_of_rent : collected tenant rent × fee%
  const marginNow = isPct
    ? occupiedRent * (feePct / 100)
    : occupiedRent - masterRent;
  const marginAtFull = isPct
    ? totalListedRent * (feePct / 100)
    : totalListedRent - masterRent;

  return (
    <div className="space-y-4">
      <Card className="p-6 space-y-4">
        <header className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold">
              {t("admin.section.activeRevenueModel", "Active revenue model")}
            </h2>
            <p className="text-xs text-muted-foreground">
              {isOperator
                ? `How ${platformName} earns from this property.`
                : t(
                    "admin.revenue.headerHint",
                    "How coliviq earns from this property."
                  )}
            </p>
          </div>
          <Badge>
            {isPct
              ? t("admin.section.pctOfRent", "Percentage of rent")
              : t("admin.section.fullRent", "Full rent")}
          </Badge>
        </header>
        <p className="text-sm text-muted-foreground">
          {isPct
            ? isOperator
              ? `${platformName} takes a fixed percentage of tenant rent collections; the owner keeps the rest. Margin = collected tenant rent × ${platformName} fee.`
              : t(
                  "admin.revenue.pctOfRentDescDetail",
                  "coliviq takes a fixed percentage of tenant rent collections; the owner keeps the rest. Margin = collected tenant rent × coliviq fee."
                )
            : isOperator
              ? `${platformName} pays the owner a fixed master rent and keeps whatever it collects from tenants. Margin = collected tenant rent − master rent.`
              : t(
                  "admin.revenue.fullRentDescDetail",
                  "coliviq pays the owner a fixed master rent and keeps whatever it collects from tenants. Margin = collected tenant rent − master rent."
                )}
        </p>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        {isPct ? (
          <Card className="p-5 space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              {isOperator
                ? `${platformName} fee (%)`
                : t("admin.field.coliviqFeePct", "coliviq fee (%)")}
            </h3>
            <div className="text-2xl font-semibold tabular-nums">
              {property.coliviq_fee_pct != null
                ? `${property.coliviq_fee_pct}%`
                : "—"}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("admin.revenue.ownerKeeps", "Owner keeps")}{" "}
              {property.coliviq_fee_pct != null
                ? `${(100 - Number(property.coliviq_fee_pct)).toFixed(2).replace(/\.?0+$/, "")}%`
                : "—"}
            </p>
          </Card>
        ) : (
          <Card className="p-5 space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              {isOperator
                ? `${platformName} pays out`
                : t("admin.revenue.coliviqPaysOut", "coliviq pays out")}
            </h3>
            <div className="text-2xl font-semibold tabular-nums">
              {formatEUR(masterRent) ?? "—"}
            </div>
            <p className="text-xs text-muted-foreground">
              {t("admin.revenue.perMonthToOwner", "/ month, to the owner")}
            </p>
          </Card>
        )}

        <Card className="p-5 space-y-2">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
            {isOperator
              ? `${platformName} collects (today)`
              : t("admin.revenue.coliviqCollects", "coliviq collects (today)")}
          </h3>
          <div className="text-2xl font-semibold tabular-nums">
            {formatEUR(occupiedRent) ?? "—"}
          </div>
          <p className="text-xs text-muted-foreground">
            {t("admin.revenue.sumOccupied", "Sum of rent from occupied rooms")}
          </p>
        </Card>
      </div>

      <Card className="p-5 space-y-3">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          {t("admin.revenue.estimatedMargin", "Estimated margin")}
        </h3>
        <div className="space-y-3">
          <MarginRow
            label={t("admin.field.rightNow", "Right now")}
            value={marginNow}
          />
          <MarginRow
            label={t("admin.field.atFullOccupancy", "At full occupancy")}
            value={marginAtFull}
            hint={`Assumes all ${rooms.filter((r) => !r.is_hidden).length} rooms occupied at listed prices`}
          />
        </div>
        {!isPct && !property.master_rent && (
          <p className="text-xs text-muted-foreground italic pt-2 border-t">
            {t(
              "admin.revenue.setMasterRentHint",
              "Set the master rent to see real margin numbers."
            )}
          </p>
        )}
        {isPct && property.coliviq_fee_pct == null && (
          <p className="text-xs text-muted-foreground italic pt-2 border-t">
            {isOperator
              ? `Set the ${platformName} fee % to see real margin numbers.`
              : t(
                  "admin.revenue.setFeePctHint",
                  "Set the coliviq fee % to see real margin numbers."
                )}
          </p>
        )}
      </Card>
    </div>
  );
}

function MarginRow({
  label,
  value,
  hint,
}: {
  label: string;
  value: number;
  hint?: string;
}) {
  const trend = value > 0 ? "up" : value < 0 ? "down" : "flat";
  const Icon =
    trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : Minus;
  const colorClass =
    trend === "up"
      ? "text-emerald-600"
      : trend === "down"
        ? "text-destructive"
        : "text-muted-foreground";

  return (
    <div className="space-y-0.5">
      <div className="flex items-baseline justify-between">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span
          className={`text-base font-semibold tabular-nums flex items-center gap-1 ${colorClass}`}
        >
          <Icon className="h-3.5 w-3.5" />
          {formatEUR(value) ?? "—"}
        </span>
      </div>
      {hint && <p className="text-xs text-muted-foreground/80">{hint}</p>}
    </div>
  );
}
